import EnumDataSource from "../utils/enum-data-source.util";

export const PAYER_TYPE_PATIENT = 1;
export const PAYER_TYPE_INSURER = 2;

export const payerTypes = {
  [PAYER_TYPE_PATIENT]: "Patient",
  [PAYER_TYPE_INSURER]: "Insurer"
};

export const PAYER_TYPES = new EnumDataSource(payerTypes);

export const DEFAULT_PAYMENT_METHOD = 1;
export const CASH_PAYMENT_METHOD = 1;
export const CARD_PAYMENT_METHOD = 2;
export const DIRECT_DEPOSIT_PAYMENT_METHOD = 3;
export const CHECK_PAYMENT_METHOD = 4;
export const CARE_CREDIT_PAYMENT_METHOD = 5;
export const HEALTHIPLAN_PAYMENT_METHOD = 6;
export const WELLS_FARGO_PAYMENT_METHOD = 7;
export const ONLINE_PAYMENT_METHOD = 8;
export const EFT_PAYMENT_METHOD = 9;
export const PATIENT_CREDIT_PAYMENT_METHOD = 10;
export const ALLEGRO_PAYMENT_METHOD = 11;
export const VISA_PAYMENT_METHOD = 12;
export const MASTERCARD_PAYMENT_METHOD = 13;
export const AMERICAN_EXPRESS_PAYMENT_METHOD = 14;
export const DISCOVER_PAYMENT_METHOD = 15;
export const ACH_METHOD = 16;
export const ZELLE_PAYMENT_METHOD = 17;
export const VENMO_PAYMENT_METHOD = 18;
export const CHERRY_PAYMENT_METHOD = 19;
export const SQUARE_PAYMENT_METHOD = 20;
// Spends credit the practice holds on an insurer's behalf. Written only by the insurer-credit
// application endpoint(s) — the ordinary payment endpoints 400 it, and the backend rejects it in
// `Practice.billing_available_payment_methods`, so it can never come back from
// `useGetPaymentsMethodTypeOptions`. Two screens append it by hand once the sale is actually
// eligible (isPaymentSourceEligibleForInsuranceCredit) via the shared
// `useGetPaymentsMethodTypeOptionsWithInsuranceCredit` hook: the take-payment method dropdown
// (`payment-details-form-part.component.tsx`) and the Insurance Payments/Adjustments form's method
// dropdown (ENG-1741/D12). It is in `paymentMethods` purely so every read surface (payment rows,
// statements, invoices, merge tags) has a label for it. SN-19061.
export const INSURANCE_CREDIT_PAYMENT_METHOD = 21;
export const OTHER_PAYMENT_METHOD = 99;

export const paymentMethods = {
  [ACH_METHOD]: "ACH",
  [ALLEGRO_PAYMENT_METHOD]: "Allegro",
  [AMERICAN_EXPRESS_PAYMENT_METHOD]: "American Express",
  [CARD_PAYMENT_METHOD]: "Credit/Debit Card",
  [CARE_CREDIT_PAYMENT_METHOD]: "Financing - CareCredit",
  [CASH_PAYMENT_METHOD]: "Cash",
  [CHERRY_PAYMENT_METHOD]: "Financing - Cherry",
  [CHECK_PAYMENT_METHOD]: "Check",
  [DIRECT_DEPOSIT_PAYMENT_METHOD]: "Direct Deposit",
  [DISCOVER_PAYMENT_METHOD]: "Discover",
  [EFT_PAYMENT_METHOD]: "EFT",
  [HEALTHIPLAN_PAYMENT_METHOD]: "Financing - HealthiPlan",
  [INSURANCE_CREDIT_PAYMENT_METHOD]: "Insurance Credit",
  [MASTERCARD_PAYMENT_METHOD]: "Mastercard",
  [ONLINE_PAYMENT_METHOD]: "Online",
  [OTHER_PAYMENT_METHOD]: "Other",
  [PATIENT_CREDIT_PAYMENT_METHOD]: "Patient Credit",
  [SQUARE_PAYMENT_METHOD]: "Square",
  [VENMO_PAYMENT_METHOD]: "Venmo",
  [VISA_PAYMENT_METHOD]: "Visa",
  [WELLS_FARGO_PAYMENT_METHOD]: "Financing - Wells Fargo",
  [ZELLE_PAYMENT_METHOD]: "Zelle"
};

export const PAYMENT_METHODS = new EnumDataSource(paymentMethods);

// --- Card brand / last 4 (internal reconciliation) -------------------------------------------
//
// These are the four brands staff can PICK. The backend column deliberately accepts the wider
// `CardBrand` set (diners, jcb, maestro, visa_debit, …) because a Suno-charged card can come back
// from the processor as any of them — so always render a stored brand through
// `getCardBrandLabel`, which falls back gracefully, rather than assuming it is one of these four.
export const CARD_BRAND_VISA = "visa";
export const CARD_BRAND_MASTERCARD = "mastercard";
export const CARD_BRAND_AMEX = "amex";
export const CARD_BRAND_DISCOVER = "discover";

export const cardBrands = {
  [CARD_BRAND_VISA]: "Visa",
  [CARD_BRAND_MASTERCARD]: "Mastercard",
  [CARD_BRAND_AMEX]: "American Express",
  [CARD_BRAND_DISCOVER]: "Discover"
};

export const CARD_BRANDS = new EnumDataSource(cardBrands, {
  [CARD_BRAND_VISA]: 1,
  [CARD_BRAND_MASTERCARD]: 2,
  [CARD_BRAND_AMEX]: 3,
  [CARD_BRAND_DISCOVER]: 4
});

// Labels for brands the backend can store but the picker does not offer, so a processor-populated
// payment never renders as a raw enum value like "visa_debit" in the reconciliation columns.
const EXTRA_CARD_BRAND_LABELS: { [code: string]: string } = {
  diners: "Diners Club",
  jcb: "JCB",
  maestro: "Maestro",
  solo: "Solo",
  visa_debit: "Visa Debit",
  visa_electron: "Visa Electron",
  unknown: "Unknown"
};

export const getCardBrandLabel = (brand?: string | null): string => {
  if (!brand) return "";
  return (
    CARD_BRANDS.getFromCode(brand) || EXTRA_CARD_BRAND_LABELS[brand] || brand
  );
};

// Every brand the backend column can hold, for FILTER surfaces (the payments report Card Brand
// filter). Unlike the payment-form picker, a filter limited to the four staff-pickable brands
// could never find rows a Suno-charged card wrote as jcb/diners/visa_debit/… — so the long tail
// and Unknown are offered too, picker brands first. Note "Unknown" matches only rows STORED as
// 'unknown' (a processor-written value) — blank/never-recorded brands are not reachable by any
// option here.
export const CARD_BRAND_FILTER_OPTIONS: { value: string; label: string }[] = [
  ...CARD_BRANDS.getOptions(),
  ...Object.entries(EXTRA_CARD_BRAND_LABELS).map(([value, label]) => ({
    value,
    label
  }))
];

// Options for the Card Brand picker, given the brand already stored on the row.
//
// A Suno-charged payment can carry a brand the picker never offers (visa_debit, jcb, …). MUI Select
// renders an out-of-range value as an EMPTY box, so staff open a payment whose brand is Visa Debit,
// see a blank field next to a populated Last 4, and "fix" it — overwriting real processor data.
// Appending the stored brand keeps it visible and selected; the four picker brands stay first.
export const getCardBrandOptions = (
  currentBrand?: string | null
): { value: string; label: string }[] => {
  const options = CARD_BRANDS.getOptions();
  const brand = currentBrand?.trim();
  if (!brand || options.some((option) => option.value === brand)) {
    return options;
  }
  return [...options, { value: brand, label: getCardBrandLabel(brand) }];
};

// Payment methods that get the Card Brand / Card Last 4 inputs. Covers both the Suno-charged card
// method and the brand-named legacy methods clinics use when the card was run outside Suno — which
// is exactly the population this feature exists for.
export const CARD_DETAIL_PAYMENT_METHODS = [
  CARD_PAYMENT_METHOD,
  VISA_PAYMENT_METHOD,
  MASTERCARD_PAYMENT_METHOD,
  AMERICAN_EXPRESS_PAYMENT_METHOD,
  DISCOVER_PAYMENT_METHOD
];

export const methodTakesCardDetails = (method?: number | null): boolean =>
  method != null && CARD_DETAIL_PAYMENT_METHODS.includes(method);

export const CARD_LAST4_PATTERN = /^\d{4}$/;

// Build the card-detail slice of a payment request body.
//
// Keys are OMITTED when empty rather than sent as null. The backend serializer rejects unknown
// write keys outright, so an unconditional key would 400 EVERY payment create against a backend
// that predates these fields. Omitting when empty means a frontend that somehow ships ahead of the
// backend only fails when someone actually fills the fields in, instead of breaking all payments.
export const buildCardDetailsPayload = (values: {
  method?: number | null;
  card_brand?: string | null;
  card_last4?: string | null;
}): { card_brand?: string; card_last4?: string } => {
  if (!methodTakesCardDetails(values.method)) return {};
  const payload: { card_brand?: string; card_last4?: string } = {};
  const brand = values.card_brand?.trim();
  const last4 = values.card_last4?.trim();
  if (brand) payload.card_brand = brand;
  if (last4) payload.card_last4 = last4;
  return payload;
};

// Edit counterpart of buildCardDetailsPayload. On an UPDATE staff must be able to CLEAR a value, and
// an OMITTED key cannot express that — PATCH leaves an omitted field untouched. So both keys are
// always sent, null when blank.
//
// That includes non-card methods: switching a Visa payment to Cash must actually REMOVE the stored
// brand/last 4, otherwise the row keeps rendering "Cash — Visa — 4242" on the invoice and in the
// Payments report. Omitting the keys there (the original approach) preserved exactly the stale
// values it was meant to drop.
//
// Callers must still gate this behind PAYMENT_CARD_BRAND_LAST4: with the flag off the keys are not
// sent at all, so the edit can't 400 against a backend that predates the fields.
export const buildCardDetailsUpdatePayload = (values: {
  method?: number | null;
  card_brand?: string | null;
  card_last4?: string | null;
}): { card_brand: string | null; card_last4: string | null } => {
  if (!methodTakesCardDetails(values.method)) {
    return { card_brand: null, card_last4: null };
  }
  return {
    card_brand: values.card_brand?.trim() || null,
    card_last4: values.card_last4?.trim() || null
  };
};

export const PATIENT_PAYER_TYPE = PAYER_TYPE_PATIENT;
export const TILLED_PAYMENT_METHOD = 1;
export const JUSTIFI_PAYMENT_METHOD = 2;
export const GRAVITY_PAYMENT_METHOD = 3;
export const PAYMENT_LIST_KEY_DEFAULT = "paymentList";

// Resolve a clinic's card processor: the clinic's own value is authoritative, falling back
// to the practice's only when the clinic hasn't set one. Mirrors the backend's
// clinic→practice resolution (get_gravity_client). Single source of truth so the Gravity and
// Tilled gates can't drift apart on the clinic-vs-practice question.
export const resolveCardProcessingProvider = (clinic?: {
  card_processing_provider?: number;
  practice?: { card_processing_provider?: number } | null;
}): number | undefined =>
  clinic?.card_processing_provider ??
  clinic?.practice?.card_processing_provider;

// A surcharge rate is "set" when it is neither null/undefined nor an empty string. An explicit 0 IS
// a real value (the clinic surcharges nothing) and must NOT fall back to the practice — only a
// missing/empty rate inherits. Mirrors the backend's `is not None` check.
const hasSurchargeRate = (value?: string | number | null): boolean =>
  value !== null && value !== undefined && value !== "";

// Resolve a clinic's credit-card surcharge rate (percent): the clinic's own rate wins (including an
// explicit 0), otherwise it inherits the practice rate. Mirrors the backend
// Clinic.effective_surcharge_percent so payment-time estimates reflect a per-clinic override instead
// of always showing the practice-wide rate. Prefers the backend-resolved `effective_surcharge_percent`
// when the payload carries it (it already encodes this same rule).
export const resolveSurchargePercent = (clinic?: {
  surcharge_percent?: string | number | null;
  effective_surcharge_percent?: string | number | null;
  practice?: { surcharge_percent?: string | number | null } | null;
}): number => {
  if (!clinic) return 0;
  if (hasSurchargeRate(clinic.effective_surcharge_percent)) {
    return Number(clinic.effective_surcharge_percent) || 0;
  }
  if (hasSurchargeRate(clinic.surcharge_percent)) {
    return Number(clinic.surcharge_percent) || 0;
  }
  return Number(clinic.practice?.surcharge_percent) || 0;
};

// Whether the clinic (with practice fallback) is actually set up to process card payments for
// its resolved processor. Tilled requires an account id at the clinic or practice level — without
// it the clinic cannot process payments even though a provider may be resolved. Mirrors the
// clinic→practice fallback used everywhere else so the "enabled" gate can't drift.
export const isCardProcessingConfigured = (clinic?: {
  card_processing_provider?: number;
  tilledAccountId?: string;
  practice?: {
    card_processing_provider?: number;
    tilled_account_id?: string;
  } | null;
}): boolean => {
  if (resolveCardProcessingProvider(clinic) === GRAVITY_PAYMENT_METHOD) {
    return true;
  }
  return Boolean(
    clinic?.tilledAccountId ?? clinic?.practice?.tilled_account_id
  );
};

// Keep only the given processor's readers. A reader without the field (payload cached
// before V2.2) is always kept — a missing field must never empty the dropdown.
export const filterReadersByProvider = <
  T extends { card_processing_provider?: number }
>(
  readers: T[],
  provider?: number
): T[] =>
  provider == null
    ? readers
    : readers.filter(
        (reader) =>
          reader.card_processing_provider == null ||
          reader.card_processing_provider === provider
      );

export const MINIMUM_CREDIT_AMOUNT = 0.1;

export const CHARGE_METHOD_TERMINAL_VALUE = 1;
export const CHARGE_METHOD_ONLINE_VALUE = 2;

const chargeMethods = {
  [CHARGE_METHOD_TERMINAL_VALUE]: "Terminal Payment",
  [CHARGE_METHOD_ONLINE_VALUE]: "Non Terminal Payment"
};

export const CHARGE_METHODS = new EnumDataSource(chargeMethods);

export const ACH_DEBIT_ACCOUNT_TYPE_CHECKING = "checking";
export const ACH_DEBIT_ACCOUNT_TYPE_SAVINGS = "savings";

const achDebitAccountTypes = {
  [ACH_DEBIT_ACCOUNT_TYPE_CHECKING]: "Checking",
  [ACH_DEBIT_ACCOUNT_TYPE_SAVINGS]: "Savings"
};

export const ACH_DEBIT_ACCOUNT_TYPES = new EnumDataSource(achDebitAccountTypes);

export const DEFAULT_MAX_SUBSCRIPTION_PAYMENTS = 12;

// Which backend error sentences a staff payment screen may show. An allowlist, not a passthrough:
// supplying a filter at all clears ErrorSnackbarContent's prod suppression, and the extractor reads
// every DRF error — auth and validation detail must stay hidden. These patterns match the sentences
// the backend writes FOR staff about a clinic's payment setup (ENG-1806).
export const STAFF_ACTIONABLE_PAYMENT_ERROR =
  /surcharge|suno support|payment processor|payment processing/i;
